/**
 * Clipboard Copy Logic
 *
 * Pure functions for copying grid data to clipboard.
 */

import { CLIPBOARD_FAILED, warnDiagnostic } from '../../core/internal/diagnostics';
import { resolveCellValue } from '../../core/internal/value-accessor';
import type { ColumnConfig } from '../../core/types';
import { formatDelimitedValue, type DelimitedFormatOptions } from '../shared/data-collection';
import type { ClipboardConfig } from './types';

/** Parameters for building clipboard text */
export interface CopyParams {
  /** All grid rows */
  rows: unknown[];
  /** Column configurations */
  columns: ColumnConfig[];
  /** Selected row indices */
  selectedIndices: Set<number> | number[];
  /** Clipboard configuration */
  config: ClipboardConfig;
}

/**
 * Format a cell value for clipboard output.
 *
 * Uses custom processCell if provided, otherwise applies default formatting:
 * - null/undefined → empty string
 * - Date → ISO string
 * - Object → JSON string
 * - Other → String conversion with optional quoting
 *
 * @param value - The cell value to format
 * @param field - The field name
 * @param row - The full row object
 * @param config - Clipboard configuration
 * @returns Formatted string value
 */
export function formatCellValue(value: unknown, field: string, row: unknown, config: ClipboardConfig): string {
  if (config.processCell) {
    return config.processCell(value, field, row);
  }
  return formatDelimitedValue(value, toFormatOptions(config));
}

/** Map the plugin config onto the shared delimited-format options. */
function toFormatOptions(config: ClipboardConfig): DelimitedFormatOptions {
  return {
    delimiter: config.delimiter ?? '\t',
    newline: config.newline ?? '\n',
    quoting: config.quoteStrings ? 'always' : 'auto',
    escapeFormulas: config.escapeFormulas,
  };
}

/**
 * Build clipboard text from selected rows and columns.
 *
 * @param params - Copy parameters including rows, columns, selection, and config
 * @returns Tab-separated (or custom delimiter) text ready for clipboard
 */
export function buildClipboardText(params: CopyParams): string {
  const { rows, columns, selectedIndices, config } = params;
  const delimiter = config.delimiter ?? '\t';
  const newline = config.newline ?? '\n';
  // Built once — this runs per cell across the whole selection.
  const format = toFormatOptions(config);

  // Filter to visible columns (not hidden, not internal __ prefixed)
  const visibleColumns = columns.filter((c) => !c.hidden && !c.field.startsWith('__'));

  const lines: string[] = [];

  // Add header row if configured
  if (config.includeHeaders) {
    const headerCells = visibleColumns.map((c) => {
      const header = c.header || c.field;
      // Quote headers if they contain special characters
      if (header.includes(delimiter) || header.includes(newline) || header.includes('"')) {
        return `"${header.replace(/"/g, '""')}"`;
      }
      return header;
    });
    lines.push(headerCells.join(delimiter));
  }

  // Convert indices to sorted array
  const indices = selectedIndices instanceof Set ? [...selectedIndices] : selectedIndices;
  const sortedIndices = [...indices].sort((a, b) => a - b);

  // Build data rows
  for (const idx of sortedIndices) {
    const row = rows[idx];
    if (!row) continue;

    const cells = visibleColumns.map((col) => {
      const value = resolveCellValue(row, col);
      return config.processCell
        ? config.processCell(value, col.field, row)
        : formatDelimitedValue(value, format);
    });
    lines.push(cells.join(delimiter));
  }

  return lines.join(newline);
}

/**
 * Copy text to the system clipboard.
 *
 * Uses the modern Clipboard API when available, with fallback
 * to execCommand for older browsers.
 *
 * When `html` is supplied, writes BOTH `text/plain` and `text/html` via
 * `ClipboardItem` so a same-app paste (even cross-grid or cross-window) can
 * recover a structured payload embedded in the HTML, while external targets
 * (Excel, editors) still get the plain text. Silently falls back to text-only
 * when `ClipboardItem` / `clipboard.write` is unavailable or rejects.
 *
 * @param text - The text to copy (`text/plain`)
 * @param html - Optional rich `text/html` representation (may embed a payload)
 * @returns Promise resolving to true if successful, false otherwise
 */
export async function copyToClipboard(text: string, html?: string): Promise<boolean> {
  if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);
      return true;
    } catch {
      // Fall through to text-only write below.
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    warnDiagnostic(CLIPBOARD_FAILED, `Clipboard API failed: ${err}`);
    // Fallback for older browsers or when Clipboard API is not available
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  }
}
